<!-- Full-width relative wrapper anchors the dropdown to the sidebar edges. -->
<div class="relative w-full" *transloco="let t">
  <!-- DS LeftMenu portal trigger: full-width 48px, hairlines top+bottom,
       org name uppercase (Figtree 700 16/20, tracked), caret on right. -->
  <button
    type="button"
    [disabled]="single()"
    (click)="toggle()"
    class="group flex w-full items-center justify-center gap-1 px-4 enabled:cursor-pointer enabled:hover:bg-sunken"
    style="height:48px"
    aria-haspopup="listbox"
    [attr.aria-expanded]="open()"
    [attr.aria-label]="t('tenantSwitcher.switchTenant')"
  >
    <span
      class="min-w-0 truncate font-display text-[16px] font-bold uppercase leading-5 tracking-[0.03em] text-ink-1"
    >
      {{ currentView()?.name || t('tenantSwitcher.noOrganization') }}
    </span>

    @if (!single()) {
      <app-icon
        class="shrink-0 text-ink-2"
        [name]="open() ? 'icon-arrow-collapse' : 'icon-arrow-dropdown'"
        [size]="24"
      />
    }
  </button>

  <!-- One option row, shared by the plain menu, the Recent block and the
       grouped list. Compact (no sub-line) in the plain menu. -->
  <ng-template #optionRow let-tile let-compact="compact">
    <div
      role="option"
      [attr.aria-selected]="tile.active"
      (click)="select(tile.id)"
      (keydown.enter)="select(tile.id); $event.preventDefault()"
      (keydown.space)="select(tile.id); $event.preventDefault()"
      tabindex="0"
      class="ts-row flex min-h-12 cursor-pointer items-center gap-2 rounded-[6px] py-2 pl-3 pr-2"
      [class.active]="tile.active"
    >
      <span class="flex min-w-0 grow flex-col gap-0.5">
        <span class="truncate font-body text-[15px] font-semibold leading-[18px]">
          {{ tile.name }}
        </span>
        @if (!compact && tile.alias) {
          <span class="ts-alias truncate font-num text-[11.5px] leading-[14px]">
            {{ tile.alias }}
          </span>
        }
      </span>
      @if (tile.active) {
        <app-icon class="shrink-0 text-brand" name="icon-checkmark" [size]="18" />
      }
    </div>
  </ng-template>

  @if (open() && !single()) {
    @if (!searchable()) {
      <!-- Plain menu: 5 tenants or fewer. Inset 16px from each sidebar edge,
           gap:2px + p:4px between rows, 6px row radius — matching DS design. -->
      <div
        role="listbox"
        class="absolute top-[calc(100%+4px)] left-4 right-4 z-50 flex flex-col gap-0.5 rounded-control bg-card p-1 shadow-overlay"
      >
        <div class="px-3 pb-1 pt-2">
          <span class="font-display text-[12px] font-bold uppercase leading-4 tracking-[0.03em] text-ink-2">
            {{ t('tenantSwitcher.switchTenant') }}
          </span>
        </div>
        @for (tile of tenantViews(); track tile.id) {
          <ng-container
            [ngTemplateOutlet]="optionRow"
            [ngTemplateOutletContext]="{ $implicit: tile, compact: true }"
          />
        }
      </div>
    } @else {
      <!-- Searchable panel: search field, Recent block, grouped list, count footer -->
      <div
        class="absolute top-[calc(100%+8px)] left-0 z-50 flex w-[348px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-control bg-card shadow-overlay"
      >
        <div class="border-b border-hairline p-2.5">
          <div
            class="flex h-10 items-center gap-2 rounded-control border border-hairline bg-sunken px-3"
          >
            <app-icon name="icon-search" [size]="18" class="shrink-0 text-ink-3" />
            <input
              #searchInput
              type="text"
              [value]="query()"
              (input)="onQueryInput($any($event.target).value)"
              [placeholder]="t('tenantSwitcher.searchPlaceholder', { count: count() })"
              class="w-full flex-1 border-none bg-transparent text-sm text-ink-1 outline-none"
            />
          </div>
        </div>

        <div role="listbox" class="max-h-[min(408px,54vh)] overflow-y-auto py-1">
          @if (showRecent()) {
            <div
              class="px-3 pt-2 pb-1 font-display text-[11px] font-semibold uppercase tracking-wide text-ink-3"
            >
              {{ t('tenantSwitcher.recent') }}
            </div>
            @for (tile of recentViews(); track tile.id) {
              <ng-container
                [ngTemplateOutlet]="optionRow"
                [ngTemplateOutletContext]="{ $implicit: tile }"
              />
            }
            <div class="my-1.5 h-px bg-hairline"></div>
          }

          @for (row of panelRows(); track $index) {
            @if (row.kind === 'letter') {
              <div
                class="px-3 pt-2 pb-1 font-display text-[11px] font-semibold uppercase tracking-wide text-ink-3"
              >
                {{ row.label }}
              </div>
            } @else {
              <ng-container
                [ngTemplateOutlet]="optionRow"
                [ngTemplateOutletContext]="{ $implicit: row.tile }"
              />
            }
          }

          @if (matchCount() === 0) {
            <div class="px-3 py-5 text-center font-body text-sm text-ink-3">
              {{ t('tenantSwitcher.noResults') }}
            </div>
          }
        </div>

        <div
          class="flex items-center gap-2.5 border-t border-hairline bg-sunken px-3.5 py-2"
        >
          <span class="font-num text-xs whitespace-nowrap text-ink-3">
            {{
              query().trim()
                ? t('tenantSwitcher.countFiltered', { shown: matchCount(), total: count() })
                : t('tenantSwitcher.count', { count: count() })
            }}
          </span>
          <div class="flex-1"></div>
          @if (canGoAdmin()) {
            <button
              type="button"
              (click)="onGoAdmin()"
              class="cursor-pointer rounded-control px-1.5 py-1 font-body text-[12.5px] font-semibold whitespace-nowrap text-brand hover:bg-surface-2"
            >
              {{ t('tenantSwitcher.adminAllTenants') }}
            </button>
          }
        </div>
      </div>
    }
  }
</div>
